import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { FormatCurrencyPipe } from '../../../../../../shared/pipes/format-currency.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import {
  Estimate,
  Quote,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { VendorService } from '../../../../../../shared/services/vendor.service';
import {
  QuoteSowModalComponent,
  SowModalData,
} from '../quote-sow-modal/quote-sow-modal.component';
import { QuotePdfTypeModalComponent } from '../quote-pdf-type-modal/quote-pdf-type-modal.component';
import { VendorSelectBtnComponent } from '../../../../../../components/vendor-select-btn/vendor-select-btn.component';
import { DownloadLoadingDialogComponent } from '../../../../../../shared/components/download-loading-dialog/download-loading-dialog.component';
import { DocumentRequirementChangesComponent } from '../../../../../../features/job-quotes/document-requirement-changes/document-requirement-changes.component';
import { JobDetails } from '../../../../../../shared/models/job-data.model';
import {
  QuotePdfOption,
  QuoteType,
  getAvailableQuoteTypes,
  getDeliveryDateForQuoteType,
  getQuoteForType,
} from '../quote-types';

interface QuoteDetailRow {
  jobType: QuoteType;
  deliveryDate: string;
  quote: Quote | null;
}

@Component({
  selector: 'app-quote-details',
  standalone: true,
  imports: [
    FormatCurrencyPipe,
    MatIcon,
    VendorSelectBtnComponent,
    DocumentRequirementChangesComponent,
  ],
  templateUrl: './quote-details.component.html',
  styleUrl: './quote-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteDetailsComponent {
  private readonly dialog = inject(MatDialog);
  private readonly vendorService = inject(VendorService);

  readonly estimate = input.required<Estimate>();
  readonly order = input<VendorOrder>();
  readonly isLowestRate = input(false);
  readonly back = output<void>();
  readonly quoteConfirmed = output<string>();

  readonly showSelectQuote = computed(() => {
    const est = this.estimate();
    return !!est.priceMax && est.jobStatus === 'AWAITING_VENDOR_SELECTION';
  });

  readonly loading = signal(false);

  readonly vendorName = computed(
    () => this.estimate().name ?? 'Unknown Vendor',
  );

  readonly currency = computed(() => {
    const est = this.estimate();
    return toIsoCurrency(est.currency ?? est.quote?.normal?.currency);
  });

  readonly quoteId = computed(() => this.order()?.jobNumber ?? '--');

  readonly vendorJobDetails = computed<JobDetails>(() => {
    const o = this.order();
    const est = this.estimate();
    const vName = est.name ?? '';
    const vendorFiles = (o?.files ?? []).filter((f) => f.vendorName === vName);
    const revision = (o?.vendorRevisions ?? []).find(
      (vr) => vr.vendorName === vName,
    );

    return {
      files: vendorFiles,
      jobStatus: o?.jobStatus ?? '',
      quote: est.quote
        ? { acceptedBy: est.quote.acceptedBy, acceptedDate: undefined }
        : undefined,
      changeJustificationDtp: revision?.changeJustificationDtp,
    };
  });

  readonly hasAnyReclassification = computed(() => {
    const details = this.vendorJobDetails();
    return (details.files ?? []).some(
      (f) => f.isDtpFile !== f.isDtpFileRevised,
    );
  });

  readonly rows = computed<QuoteDetailRow[]>(() => {
    const est = this.estimate();
    return getAvailableQuoteTypes(est, true).map((quoteType) => {
      const deliveryDate = getDeliveryDateForQuoteType(est, quoteType);
      return {
        jobType: quoteType,
        deliveryDate: deliveryDate
          ? this.formatDateDDMonYYYY(deliveryDate)
          : '--',
        quote: getQuoteForType(est, quoteType),
      };
    });
  });

  readonly quotePdfOptions = computed<QuotePdfOption[]>(() => {
    const est = this.estimate();
    return getAvailableQuoteTypes(est, true).map((quoteType) => {
      const quote = getQuoteForType(est, quoteType);
      const deliveryDate = getDeliveryDateForQuoteType(est, quoteType);
      return {
        quoteType,
        deliveryDate: deliveryDate
          ? this.formatDateDDMonYYYY(deliveryDate)
          : '--',
        totalCost: quote?.totalCost ?? est.priceMax ?? est.priceMin ?? 0,
        currency: this.currency(),
      };
    });
  });

  getCostBreakdown(quote: Quote | null): { concept: string; cost: number }[] {
    if (!quote?.costs?.length) return [];
    return quote.costs;
  }

  getTotalCost(quote: Quote | null): number {
    if (quote?.totalCost != null) return quote.totalCost;
    const est = this.estimate();
    return est.priceMax ?? est.priceMin ?? 0;
  }

  getRecommendedPOTotal(quote: Quote | null): number {
    return Math.ceil(this.getTotalCost(quote) * 1.15 * 100) / 100;
  }

  async generateQuotePDF(): Promise<void> {
    const order = this.order();
    if (!order) {
      return;
    }

    const selectedQuoteType = await this.resolveSelectedQuoteType();
    if (!selectedQuoteType) {
      return;
    }

    const loadingRef = this.dialog.open(DownloadLoadingDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    const modalData = await this.buildSowModalData(selectedQuoteType);

    const dialogRef = this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '65vh',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    await firstValueFrom(dialogRef.afterOpened());
    // Allow content to fully render before capturing
    await new Promise((resolve) => setTimeout(resolve, 300));
    loadingRef.close();
  }

  async openSowModal(): Promise<void> {
    const selectedQuoteType =
      getAvailableQuoteTypes(this.estimate(), true)[0] ?? 'Standard';
    const modalData = await this.buildSowModalData(selectedQuoteType);

    this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '65vh',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }

  private formatDateDDMonYYYY(dateValue: Date | string): string {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  private async resolveSelectedQuoteType(): Promise<QuoteType | null> {
    const options = this.quotePdfOptions();
    if (options.length <= 1) {
      return options[0]?.quoteType ?? 'Standard';
    }

    const dialogRef = this.dialog.open(QuotePdfTypeModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: false,
      data: { options },
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private async buildSowModalData(
    selectedQuoteType: QuoteType,
  ): Promise<SowModalData> {
    const order = this.order();
    const estimate = this.estimate();

    let breakdown;
    try {
      const targetLocaleId = Array.isArray(order?.targetLocaleId)
        ? order?.targetLocaleId.join(',')
        : (order?.targetLocaleId ?? '');

      breakdown =
        order?.jobCreatedIn === 'xtm'
          ? await this.vendorService.getXtmCostBreakdown(
              order.jobUid ?? '',
              estimate.costId ?? '',
              estimate.costBreakdownId ?? '',
            )
          : await this.vendorService.getEstimateReportBreakdown(
              order?.contentType ?? '',
              order?.sourceLocaleId ?? '',
              targetLocaleId,
              estimate.estimationsId ?? '',
            );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    return {
      order: order!,
      estimate,
      selectedQuoteType,
      breakdown,
      vendor,
    };
  }
}
